import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSystemChecksDb } from "../../helpers/fakebackend_helper";
import { APIClient } from "../../helpers/api_helper";
import * as url from "../../helpers/url_helper";

const api = new APIClient();

// Persist last results so these tables show their last-known status INSTANTLY on
// load/refresh, instead of an empty table that only fills after a re-probe. The
// row list stays put; only status / latency / last-run / message update.
const loadJson = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v == null ? fallback : v; }
  catch { return fallback; }
};
const saveJson = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota/disabled */ }
};
const DB_CACHE_KEY = "bl_db_checks_snapshot";       // { items: [...], results: {name->result} }
const INTERNAL_CACHE_KEY = "bl_internal_checks_results"; // { id -> result }
const INTERNAL_AUTORUN_KEY = "bl_internal_checks_last_autorun"; // epoch ms of last auto-probe
// Don't re-probe every data endpoint on every mount/tab-switch — the internal
// checks hit each live dashboard endpoint, so remounting (navigating away and
// back) used to fire the whole suite again. Auto-run only when the cached
// results are older than this; the "Run all" button still forces a fresh run.
const INTERNAL_AUTORUN_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Endpoints the dashboard pages actually read from. Verifying these confirms
// the data layer the live UI depends on. Each check passes if the call resolves
// (2xx) with a non-null body.
const INTERNAL_CHECKS = [
  { id: "dashboard_overview",      label: "Dashboard Overview",              path: url.GET_DASHBOARD_OVERVIEW },
  { id: "dashboard_tx_analytics",  label: "Dashboard Transaction Analytics", path: url.GET_TRANSACTION_ANALYTICS },
  { id: "metrics_dashboard",       label: "Metrics — Dashboard Aggregate",   path: url.GET_DASHBOARD_METRICS },
  { id: "metrics_summary",         label: "App Metrics — Summary",           path: url.GET_APP_METRICS_SUMMARY },
  { id: "metrics_growth",          label: "App Metrics — Growth",            path: url.GET_APP_METRICS_GROWTH },
  { id: "metrics_ios",             label: "App Metrics — iOS",               path: `${url.GET_APP_METRICS_PLATFORM}/ios` },
  { id: "metrics_android",         label: "App Metrics — Android",           path: `${url.GET_APP_METRICS_PLATFORM}/android` },
  { id: "transactions_statistics", label: "Transactions Statistics",         path: url.GET_TRANSACTION_STATISTICS },
  { id: "paybis_counts_by_flow",   label: "Paybis Counts by Flow",           path: url.GET_PAYBIS_COUNTS_BY_FLOW },
  { id: "paybis_statistics",       label: "Paybis Statistics",               path: url.GET_PAYBIS_STATISTICS },
  { id: "form_submission_stats",   label: "Form Submission Stats",           path: url.GET_FORM_SUBMISSION_STATS },
  { id: "notifications_list",      label: "Notifications List",              path: url.NOTIFICATIONS },
  { id: "alerts_admin_list",       label: "Alerts (admin)",                  path: url.GET_ALERTS_ADMIN },
];

const errMessage = (err) =>
  typeof err === "string"
    ? err
    : err?.response?.data?.message || err?.message || "Unknown error";

const formatRelative = (ts) => {
  if (!ts) return "—";
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleString();
};

const STATE_TINT = {
  ok:      { bg: "rgba(0,205,82,0.14)",   fg: "var(--bl-success-soft, #7be09a)", dot: "#2BD56F", label: "Healthy" },
  failed:  { bg: "rgba(255,93,108,0.14)", fg: "#ff8a98",                          dot: "#ff5d6c", label: "Failing" },
  skipped: { bg: "rgba(255,255,255,0.05)", fg: "#9a9caa",                         dot: "#8a8c9a", label: "Not configured" },
  running: { bg: "rgba(243,175,76,0.16)", fg: "#ffd199",                          dot: "#f3af4c", label: "Checking…" },
  pending: { bg: "rgba(255,255,255,0.04)", fg: "var(--bl-text-soft)",             dot: "#666",    label: "Not run" },
  muted:   { bg: "rgba(255,255,255,0.04)", fg: "#9a9caa",                         dot: "#666",    label: "Muted" },
};

const StatusPill = ({ state }) => {
  const tint = STATE_TINT[state] || STATE_TINT.pending;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: tint.bg,
        color: tint.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: tint.dot,
          boxShadow: state === "running" ? `0 0 0 4px ${tint.dot}33` : "none",
        }}
      />
      {tint.label}
    </span>
  );
};

const SectionPanel = ({ title, description, icon, count, okCount, failedCount, skippedCount = 0, action, children }) => (
  <div className="bl-glass" style={{ padding: 0, marginBottom: "1.25rem", overflow: "hidden" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "1rem 1.25rem",
        borderBottom: "1px solid var(--bl-line)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: "rgba(87,88,252,0.16)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ color: "var(--bl-accent)", fontSize: 17 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3
          style={{
            margin: 0,
            fontFamily: "var(--bl-font-display)",
            fontSize: "1.02rem",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {title}
        </h3>
        {description ? (
          <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "var(--bl-text-soft)" }}>{description}</p>
        ) : null}
      </div>
      <div style={{ fontSize: 12, color: "var(--bl-text-soft)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <span style={{ color: "var(--bl-success-soft, #7be09a)" }}>{okCount}</span> ok
        {failedCount > 0 && (
          <>
            {" · "}
            <span style={{ color: "#ff8a98" }}>{failedCount}</span> failing
          </>
        )}
        {skippedCount > 0 && (
          <>
            {" · "}
            <span style={{ color: "#9a9caa" }}>{skippedCount}</span> not configured
          </>
        )}
        {" · "}
        {count} total
      </div>
      {action ? <div style={{ marginLeft: "0.85rem" }}>{action}</div> : null}
    </div>
    <div style={{ overflowX: "auto" }}>{children}</div>
  </div>
);

const TableHeader = () => (
  <thead>
    <tr style={{ background: "rgba(var(--bl-accent-rgb), 0.06)", borderBottom: "1px solid var(--bl-line-strong)" }}>
      {["Name", "Status", "Time", "Last run", "Message", ""].map((h, i) => (
        <th
          key={i}
          style={{
            padding: "0.65rem 1rem",
            textAlign: i === 5 ? "right" : "left",
            fontSize: 11,
            color: "var(--bl-text-soft)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {h}
        </th>
      ))}
    </tr>
  </thead>
);

const CheckRow = ({ name, subtitle, icon, result, onRun, onToggleIgnore }) => {
  const isIgnored = !!result?.is_ignored;
  const rawState = result?.state || "pending";
  const state = isIgnored ? "muted" : rawState;
  return (
    <tr style={{ borderBottom: "1px solid var(--bl-line)", opacity: isIgnored ? 0.5 : 1, filter: isIgnored ? "grayscale(0.6)" : "none" }}>
      <td style={{ padding: "0.85rem 1rem", minWidth: 220 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {icon ? (
            <i className={icon} style={{ color: "var(--bl-text-soft)", fontSize: 16, flexShrink: 0 }} />
          ) : null}
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.2 }}>{name}</div>
            {subtitle ? (
              <div
                style={{
                  marginTop: 2,
                  fontSize: 11,
                  color: "var(--bl-text-soft)",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 360,
                }}
                title={subtitle}
              >
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td style={{ padding: "0.85rem 1rem" }}>
        <StatusPill state={state} />
      </td>
      <td
        style={{
          padding: "0.85rem 1rem",
          color: "var(--bl-text-soft)",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {result?.durationMs != null ? `${result.durationMs} ms` : "—"}
      </td>
      <td style={{ padding: "0.85rem 1rem", color: "var(--bl-text-soft)", fontSize: 12, whiteSpace: "nowrap" }}>
        {result?.ranAt ? formatRelative(result.ranAt) : "—"}
      </td>
      <td style={{ padding: "0.85rem 1rem", color: state === "failed" ? "#ff8a98" : "var(--bl-text-soft)", fontSize: 12, maxWidth: 320 }}>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={result?.message || ""}
        >
          {result?.message || (state === "ok" ? "—" : "")}
        </div>
      </td>
      <td style={{ padding: "0.85rem 1rem", textAlign: "right", whiteSpace: "nowrap" }}>
        {onToggleIgnore && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleIgnore(); }}
            title={isIgnored ? "Re-include this probe in the failing count" : "Hide this probe from the failing count"}
            style={{
              padding: "5px 9px",
              borderRadius: 6,
              border: "1px solid var(--bl-line-strong, #2b2c3f)",
              background: isIgnored ? "rgba(87,88,252,0.16)" : "rgba(255,255,255,0.03)",
              color: "var(--bl-text-strong, #e5e7f2)",
              fontSize: 12,
              cursor: "pointer",
              marginRight: 6,
            }}
          >
            <i className={`ri-${isIgnored ? "volume-up-line" : "volume-mute-line"}`} style={{ marginRight: 4, fontSize: 12 }} />
            {isIgnored ? "Unmute" : "Ignore"}
          </button>
        )}
        <button
          type="button"
          onClick={onRun}
          disabled={state === "running"}
          style={{
            padding: "5px 11px",
            borderRadius: 6,
            border: "1px solid var(--bl-line-strong, #2b2c3f)",
            background: "rgba(255,255,255,0.03)",
            color: "var(--bl-text-strong, #e5e7f2)",
            fontSize: 12,
            cursor: state === "running" ? "not-allowed" : "pointer",
            opacity: state === "running" ? 0.5 : 1,
            whiteSpace: "nowrap",
            transition: "background 120ms",
          }}
          onMouseEnter={(e) => {
            if (state !== "running") e.currentTarget.style.background = "rgba(87,88,252,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.03)";
          }}
        >
          <i className={`ri-${state === "running" ? "loader-4-line" : "refresh-line"}`} style={{ marginRight: 4, fontSize: 12 }} />
          Re-run
        </button>
      </td>
    </tr>
  );
};

const summaryFor = (resultsById, ids) => {
  let ok = 0;
  let failed = 0;
  let skipped = 0;
  for (const id of ids) {
    const r = resultsById[id];
    if (r?.is_ignored) continue;          // muted services don't count either way
    if (r?.state === "ok") ok++;
    else if (r?.state === "failed") failed++;
    else if (r?.state === "skipped") skipped++;   // not configured — neither healthy nor failing
  }
  return { ok, failed, skipped, total: ids.length };
};

const runAllButton = (label, onClick, busy) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "0.45rem 0.9rem",
      borderRadius: 8,
      border: "1px solid var(--bl-line-strong, #2b2c3f)",
      background: "rgba(87,88,252,0.16)",
      color: "var(--bl-text-strong, #e5e7f2)",
      fontSize: 13,
      fontWeight: 600,
      cursor: busy ? "not-allowed" : "pointer",
      opacity: busy ? 0.6 : 1,
      whiteSpace: "nowrap",
    }}
  >
    <i className={`ri-${busy ? "loader-4-line" : "play-circle-line"}`} style={{ fontSize: 15 }} />
    {label}
  </button>
);

// Internal checks are frontend-only reachability pings — there's no server-side
// probe/scheduler for them (unlike external services), so their mute state lives
// per-browser in localStorage rather than service_health.
const INTERNAL_IGNORE_KEY = "bl_checks_internal_ignored";

const loadInternalIgnored = () => {
  try {
    const raw = localStorage.getItem(INTERNAL_IGNORE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? Object.fromEntries(arr.map((id) => [id, true])) : {};
  } catch (_) {
    return {};
  }
};

const persistInternalIgnored = (resultsById) => {
  try {
    const ids = Object.keys(resultsById).filter((id) => resultsById[id]?.is_ignored);
    localStorage.setItem(INTERNAL_IGNORE_KEY, JSON.stringify(ids));
  } catch (_) {
    // storage disabled or over quota — mute simply won't persist this session
  }
};

// ── Internal endpoints panel ───────────────────────────────────────────────
export const InternalEndpointsPanel = () => {
  // Hydrate from the cached last results (+ mute flags) so each row shows its
  // last-known status immediately; the auto-run then refreshes in place.
  const [internal, setInternal] = useState(() => {
    const cached = loadJson(INTERNAL_CACHE_KEY, {});
    const muted = loadInternalIgnored();
    const seed = {};
    for (const [id, v] of Object.entries(cached)) {
      if (v) seed[id] = { ...v, state: v.state === "running" ? undefined : v.state };
    }
    for (const id of Object.keys(muted)) seed[id] = { ...(seed[id] || {}), is_ignored: true };
    return seed;
  });
  const [running, setRunning] = useState(false);

  // Persist results (sans transient "running") so a refresh shows last-known.
  useEffect(() => {
    const toSave = {};
    for (const [id, v] of Object.entries(internal)) {
      if (!v) continue;
      toSave[id] = { ...v, state: v.state === "running" ? undefined : v.state };
    }
    saveJson(INTERNAL_CACHE_KEY, toSave);
  }, [internal]);

  const runInternalCheck = useCallback(async (item, { silent = false } = {}) => {
    const startedAt = Date.now();
    if (!silent) setInternal((prev) => ({ ...prev, [item.id]: { ...(prev[item.id] || {}), state: "running" } }));
    try {
      const body = await api.get(item.path);
      const duration = Date.now() - startedAt;
      const ok = body !== null && body !== undefined;
      setInternal((prev) => ({
        ...prev,
        [item.id]: {
          ...(prev[item.id] || {}),     // keep is_ignored across re-runs
          state: ok ? "ok" : "failed",
          durationMs: duration,
          ranAt: Date.now(),
          message: ok ? "" : "Empty response body",
        },
      }));
    } catch (err) {
      setInternal((prev) => ({
        ...prev,
        [item.id]: {
          ...(prev[item.id] || {}),     // keep is_ignored across re-runs
          state: "failed",
          durationMs: Date.now() - startedAt,
          ranAt: Date.now(),
          message: errMessage(err),
        },
      }));
    }
  }, []);

  // Mute/unmute an internal probe. No backend call — internal checks have no
  // service_health row; the flag is persisted to localStorage so it survives a
  // reload. Muted rows drop out of the failing count via summaryFor.
  const toggleInternalIgnore = useCallback((checkId) => {
    setInternal((prev) => {
      const current = !!prev[checkId]?.is_ignored;
      const next = { ...prev, [checkId]: { ...(prev[checkId] || {}), is_ignored: !current } };
      persistInternalIgnored(next);
      return next;
    });
  }, []);

  const runAll = useCallback(async ({ silent = false } = {}) => {
    setRunning(true);
    try {
      await Promise.all(INTERNAL_CHECKS.map((c) => runInternalCheck(c, { silent })));
      saveJson(INTERNAL_AUTORUN_KEY, Date.now());
    } finally {
      setRunning(false);
    }
  }, [runInternalCheck]);

  // Refresh on mount, but only if the cached results are stale (older than
  // INTERNAL_AUTORUN_TTL_MS). Otherwise the last-known status (loaded from
  // localStorage) is shown as-is, so flipping back to this tab doesn't re-probe
  // every dashboard endpoint. Silent so cached rows don't flash to "Checking…".
  const didAutoRunRef = useRef(false);
  useEffect(() => {
    if (didAutoRunRef.current) return;
    didAutoRunRef.current = true;
    const lastRun = Number(loadJson(INTERNAL_AUTORUN_KEY, 0)) || 0;
    if (Date.now() - lastRun > INTERNAL_AUTORUN_TTL_MS) {
      runAll({ silent: true });
    }
  }, [runAll]);

  const summary = useMemo(
    () => summaryFor(internal, INTERNAL_CHECKS.map((c) => c.id)),
    [internal]
  );

  return (
    <SectionPanel
      title="Internal endpoints"
      description="The data endpoints the dashboard pages read from. A pass means 2xx with a non-empty body."
      icon="ri-router-line"
      count={INTERNAL_CHECKS.length}
      okCount={summary.ok}
      failedCount={summary.failed}
      action={runAllButton(running ? "Running…" : "Run all", runAll, running)}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <TableHeader />
        <tbody>
          {INTERNAL_CHECKS.map((item) => (
            <CheckRow
              key={item.id}
              name={item.label}
              subtitle={item.path}
              icon="ri-link"
              result={internal[item.id]}
              onRun={() => runInternalCheck(item)}
              onToggleIgnore={() => toggleInternalIgnore(item.id)}
            />
          ))}
        </tbody>
      </table>
    </SectionPanel>
  );
};

// ── Database panel ──────────────────────────────────────────────────────────
export const DatabasePanel = () => {
  // Hydrate the row list + last results from cache so the table renders
  // immediately on load; the auto-run then refreshes the cells in place.
  const [database, setDatabase] = useState(() => loadJson(DB_CACHE_KEY, {}).results || {});
  const [dbItems, setDbItems] = useState(() => loadJson(DB_CACHE_KEY, {}).items || []);
  const [running, setRunning] = useState(false);

  // silent = background refresh on mount: don't flash every row to "Checking…",
  // keep the cached last-known values visible until fresh ones land.
  const runDatabaseCheck = useCallback(async ({ silent = false } = {}) => {
    setRunning(true);
    if (!silent) {
      setDatabase((prev) => {
        const next = { ...prev };
        for (const row of dbItems) next[row.name] = { ...(next[row.name] || {}), state: "running" };
        return next;
      });
    }
    try {
      const body = await getSystemChecksDb();
      const rows = body?.checks || [];
      setDbItems(rows);
      const map = {};
      for (const row of rows) {
        map[row.name] = {
          state: row.ok ? "ok" : "failed",
          durationMs: row.durationMs,
          ranAt: Date.now(),
          message: row.message || (row.count != null ? `${row.count.toLocaleString()} rows` : ""),
        };
      }
      setDatabase(map);
      saveJson(DB_CACHE_KEY, { items: rows, results: map });
    } catch (err) {
      setDatabase((prev) => {
        const next = { ...prev };
        for (const row of dbItems) {
          next[row.name] = { state: "failed", durationMs: null, ranAt: Date.now(), message: errMessage(err) };
        }
        return next;
      });
    } finally {
      setRunning(false);
    }
  }, [dbItems]);

  // Refresh once on mount. Silent so the cached rows stay visible (no flash to
  // "Checking…") — the table is already populated from the snapshot.
  const didAutoRunRef = useRef(false);
  useEffect(() => {
    if (didAutoRunRef.current) return;
    didAutoRunRef.current = true;
    runDatabaseCheck({ silent: true });
  }, [runDatabaseCheck]);

  const summary = useMemo(() => summaryFor(database, dbItems.map((r) => r.name)), [database, dbItems]);

  return (
    <SectionPanel
      title="Database"
      description="Sequelize connection plus a row-count per KPI-critical table."
      icon="ri-database-2-line"
      count={dbItems.length || 0}
      okCount={summary.ok}
      failedCount={summary.failed}
      action={dbItems.length > 0 ? runAllButton(running ? "Running…" : "Re-run", runDatabaseCheck, running) : null}
    >
      {dbItems.length === 0 ? (
        <div
          style={{
            padding: "2rem 1.5rem",
            textAlign: "center",
            color: "var(--bl-text-soft)",
            fontSize: 13,
          }}
        >
          <div style={{ marginBottom: 12 }}>Not yet run.</div>
          <button
            type="button"
            onClick={runDatabaseCheck}
            disabled={running}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: 8,
              border: "1px solid var(--bl-line-strong, #2b2c3f)",
              background: "rgba(255,255,255,0.03)",
              color: "var(--bl-text-strong, #e5e7f2)",
              fontSize: 13,
              cursor: running ? "not-allowed" : "pointer",
              opacity: running ? 0.6 : 1,
            }}
          >
            <i className={`ri-${running ? "loader-4-line" : "play-line"}`} style={{ marginRight: 6 }} />
            {running ? "Running…" : "Run database checks"}
          </button>
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <TableHeader />
          <tbody>
            {dbItems.map((row) => (
              <CheckRow
                key={row.name}
                name={row.name}
                subtitle={row.count != null ? `${row.count.toLocaleString()} rows` : ""}
                icon="ri-table-line"
                result={database[row.name]}
                onRun={runDatabaseCheck}
              />
            ))}
          </tbody>
        </table>
      )}
    </SectionPanel>
  );
};
